// API 기본 URL 설정
const BASE_URL = process.env.REACT_APP_BASE_URL;

// 회원가입 API 호출 함수
export const registerUser = async (userData) => {
  try {
    const response = await fetch(`${BASE_URL}/api/auth/middle/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      // 에러 응답 처리
      const errorData = await response.json();
      throw new Error(errorData.message || '회원가입 중 오류가 발생했습니다.');
    }

    return await response.json();
  } catch (error) {
    console.error('회원가입 API 오류:', error);
    throw error;
  }
};

// 로그인 API 호출 함수 - OAuth2 형식 사용
export const loginUser = async (userId, password) => {
  try {
    // Form 데이터 생성
    const formData = new URLSearchParams();
    formData.append('grant_type', 'password');
    formData.append('username', userId);
    formData.append('password', password);
    formData.append('user_type', 'middle'); // 중간회원 유형 지정

    const response = await fetch(`${BASE_URL}/api/auth/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString()
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || '로그인에 실패했습니다.');
    }

    return await response.json();
  } catch (error) {
    console.error('로그인 API 오류:', error);
    throw error;
  }
};

// 플랜 결제 API 호출 함수
export const processPayment = async (planType, paymentInfo = null) => {
  try {
    const token = localStorage.getItem('token');
    
    // 플랜 타입에 따른 이름 매핑 (전체 이름으로 수정)
    const planNameMapping = {
      'basic': 'TapGo Plus 1개월 플랜',
      'premium': 'TapGo Plus 6개월 플랜',
      'enterprise': 'TapGo Plus 12개월 플랜'
    };
    
    // 요청 본문 구성
    let requestBody = {
      plan_type: planType,
      plan_name: planNameMapping[planType] || 'TapGo Plus 구독 플랜'
    };
    
    // 토스페이먼츠 결제 정보가 있으면 추가
    if (paymentInfo) {
      // paymentInfo.planName이 짧은 이름이면 전체 이름으로 변환
      let fullPlanName = paymentInfo.planName;
      if (fullPlanName && !fullPlanName.includes('TapGo Plus')) {
        fullPlanName = `TapGo Plus ${paymentInfo.planName} 플랜`;
      }
      
      // 결제 정보 추가
      requestBody = {
        ...requestBody,
        payment_key: paymentInfo.paymentKey,
        order_id: paymentInfo.orderId,
        amount: paymentInfo.amount,
        plan_name: fullPlanName || planNameMapping[planType] || 'TapGo Plus 구독 플랜'
      };
    }
    
    console.log('결제 요청 데이터:', requestBody);
    
    // API 요청 URL 확인
    const apiUrl = `${BASE_URL}/api/plan/payment/toss`;
    console.log('요청 URL:', apiUrl);
    console.log('BASE_URL:', BASE_URL);
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestBody)
    });

    console.log('응답 상태:', response.status);
    console.log('응답 URL:', response.url);
    
    // 응답 본문 (오류 발생시에도 내용 확인)
    const responseText = await response.text();
    console.log('응답 본문 텍스트:', responseText);
    
    // 응답이 JSON 형식인 경우에만 파싱
    let paymentData;
    try {
      paymentData = JSON.parse(responseText);
      console.log('응답 데이터:', paymentData);
    } catch (parseError) {
      console.error('JSON 파싱 오류:', parseError);
      throw new Error('서버 응답을 처리할 수 없습니다: ' + responseText);
    }

    if (!response.ok) {
      throw new Error(paymentData.detail || '결제 처리 중 오류가 발생했습니다.');
    }
    
    // 결제 성공 시 로그만 출력하고 저장은 하지 않음
    if (paymentData.status === 'success' && paymentData.data && paymentData.data.company_code) {
      console.log('결제 성공, 회사 코드:', paymentData.data.company_code);
    }

    return paymentData;
  } catch (error) {
    console.error('결제 API 오류:', error);
    throw error;
  }
};

// 특근 수당 설정 조회 API 함수
export const getExtraPayConfig = async (companyCode) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${BASE_URL}/api/company/extra-pay/${companyCode}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || '특근 수당 설정 조회 중 오류가 발생했습니다.');
    }

    return await response.json();
  } catch (error) {
    console.error('특근 수당 설정 조회 API 오류:', error);
    throw error;
  }
};

// 특근 수당 설정 업데이트 API 함수 추가
export const updateExtraPayConfig = async (companyCode, updateData) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${BASE_URL}/api/company/extra-pay/${companyCode}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updateData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || '특근 수당 설정 업데이트 중 오류가 발생했습니다.');
    }

    return await response.json();
  } catch (error) {
    console.error('특근 수당 설정 업데이트 API 오류:', error);
    throw error;
  }
};

// 회사 정보 조회 API 함수
export const getCompanyInfo = async (companyCode) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${BASE_URL}/api/company/info/${companyCode}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || '회사 정보 조회 중 오류가 발생했습니다.');
    }

    return await response.json();
  } catch (error) {
    console.error('회사 정보 조회 API 오류:', error);
    throw error;
  }
};

// 회사 정보 업데이트 API 함수
export const updateCompanyInfo = async (companyCode, updateData) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${BASE_URL}/api/company/info/${companyCode}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updateData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || '회사 정보 업데이트 중 오류가 발생했습니다.');
    }

    return await response.json();
  } catch (error) {
    console.error('회사 정보 업데이트 API 오류:', error);
    throw error;
  }
};

// 계약 정보 설정 조회 API 함수
export const getContractSettings = async (companyCode) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${BASE_URL}/api/company/contract/${companyCode}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || '계약 정보 설정 조회 중 오류가 발생했습니다.');
    }

    return await response.json();
  } catch (error) {
    console.error('계약 정보 설정 조회 API 오류:', error);
    throw error;
  }
};

// 계약 정보 설정 업데이트 API 함수
export const updateContractSettings = async (companyCode, updateData) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${BASE_URL}/api/company/contract/${companyCode}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updateData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      // 오류 메시지 형식 개선: 400, 500 등의 코드와 앞부분 문구 제거
      let errorMessage = errorData.detail || '계약 정보 설정 업데이트 중 오류가 발생했습니다.';
      
      // "400: ", "500: " 등의 패턴 제거
      errorMessage = errorMessage.replace(/\d{3}: /, '');
      
      // "계약 정보 설정 업데이트 중 오류가 발생했습니다: " 부분 제거
      errorMessage = errorMessage.replace('계약 정보 설정 업데이트 중 오류가 발생했습니다: ', '');
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('계약 정보 설정 업데이트 API 오류:', error);
    throw error;
  }
};

// 공지사항 조회 API 함수
export const getCompanyNotice = async (companyCode) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${BASE_URL}/api/notice/${companyCode}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || '공지사항 조회 중 오류가 발생했습니다.');
    }

    return await response.json();
  } catch (error) {
    console.error('공지사항 조회 API 오류:', error);
    throw error;
  }
};

// 공지사항 업데이트 API 함수
export const updateCompanyNotice = async (companyCode, updateData) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${BASE_URL}/api/notice/${companyCode}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updateData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || '공지사항 업데이트 중 오류가 발생했습니다.');
    }

    return await response.json();
  } catch (error) {
    console.error('공지사항 업데이트 API 오류:', error);
    throw error;
  }
};

// 공지사항 초기화 API 함수
export const resetCompanyNotice = async (companyCode) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${BASE_URL}/api/notice/reset/${companyCode}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || '공지사항 초기화 중 오류가 발생했습니다.');
    }

    return await response.json();
  } catch (error) {
    console.error('공지사항 초기화 API 오류:', error);
    throw error;
  }
};

// 월별 전 직원 출퇴근 현황 조회 API 함수 (이메일 파라미터 추가)
export const getCompanyMonthlyAttendance = async (companyCode, year, month, email = null) => {
  try {
    const token = localStorage.getItem('token');
    
    // 쿼리 파라미터 구성 (이메일 파라미터 추가)
    let url = `${BASE_URL}/api/attendance/company/monthly?year=${year}&month=${month}&include_summary=true`;
    
    // 이메일이 제공된 경우 추가
    if (email) {
      url += `&email=${encodeURIComponent(email)}`;
    }
    
    console.log('요청 URL:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    // 응답 상태 확인
    console.log('응답 상태:', response.status);
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('API 오류 데이터:', errorData);
      throw new Error(errorData.detail || '월별 직원 출퇴근 현황 조회 중 오류가 발생했습니다.');
    }

    return await response.json();
  } catch (error) {
    console.error('월별 직원 출퇴근 현황 조회 API 오류:', error);
    throw error;
  }
};

// 근무시간 수정 API 함수
export const editWorkHours = async (attendanceId, workHours, overtimeHours) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${BASE_URL}/api/attendance/edit-work-hours/${attendanceId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        work_hours: parseFloat(workHours),
        overtime_hours: parseFloat(overtimeHours),
        modification_reason: "관리자에 의한 시간 조정"
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || '근무시간 수정 중 오류가 발생했습니다.');
    }

    return await response.json();
  } catch (error) {
    console.error('근무시간 수정 API 오류:', error);
    throw error;
  }
};

// 사용자 시급 업데이트 API 함수
export const updateUserWage = async (userEmail, wage) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${BASE_URL}/api/users/wage/${userEmail}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ wage: parseFloat(wage.replace(/,/g, '')) })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || '시급 업데이트 중 오류가 발생했습니다.');
    }

    return await response.json();
  } catch (error) {
    console.error('시급 업데이트 API 오류:', error);
    throw error;
  }
};

// 월별 특이사항 업데이트 API 함수
export const updateEmployeeMonthlyNote = async (companyCode, userEmail, year, month, note) => {
  try {
    const token = localStorage.getItem('token');

    // 경로에서 앞에 /api 부분 제거
    const response = await fetch(`${BASE_URL}/api/attendance/company/${companyCode}/employee/${userEmail}/monthly-note/${year}/${month}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ note }) // MonthlyNoteIn 형식에 맞게 전송
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || '월별 특이사항 업데이트 중 오류가 발생했습니다.');
    }

    return await response.json();
  } catch (error) {
    console.error('월별 특이사항 업데이트 API 오류:', error);
    throw error;
  }
};

// 회사 월별 특이사항 조회 API 함수
export const getCompanyMonthlyNotes = async (companyCode, year, month) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${BASE_URL}/api/attendance/company/${companyCode}/monthly-notes/${year}/${month}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || '월별 특이사항 조회 중 오류가 발생했습니다.');
    }

    return await response.json();
  } catch (error) {
    console.error('월별 특이사항 조회 API 오류:', error);
    throw error;
  }
};

// 회사 직원 목록 조회 API 함수
export const getCompanyEmployees = async (emailFilter = null) => {
  try {
    const token = localStorage.getItem('token');
    
    // 쿼리 매개변수 구성
    let url = `${BASE_URL}/api/users/company/employees`;
    if (emailFilter) {
      url += `?email=${encodeURIComponent(emailFilter)}`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || '회사 직원 목록 조회 중 오류가 발생했습니다.');
    }

    return await response.json();
  } catch (error) {
    console.error('회사 직원 목록 조회 API 오류:', error);
    throw error;
  }
};

// 직원 정보 수정 API 함수
export const updateEmployeeInfo = async (userEmail, updateData) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${BASE_URL}/api/users/company/employee/${userEmail}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updateData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || '직원 정보 수정 중 오류가 발생했습니다.');
    }

    return await response.json();
  } catch (error) {
    console.error('직원 정보 수정 API 오류:', error);
    throw error;
  }
};

// 월별 출퇴근 데이터 조회 API 함수 (이메일, 년, 월 파라미터 추가)
export const getMonthlyAttendanceData = async (email, year, month) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${BASE_URL}/api/attendance/monthly-summary?email=${encodeURIComponent(email)}&year=${year}&month=${month}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || '출퇴근 데이터 조회 중 오류가 발생했습니다.');
    }

    return await response.json();
  } catch (error) {
    console.error('출퇴근 데이터 조회 API 오류:', error);
    throw error;
  }
};

// 내 플랜 정보 조회 API 함수
export const getMyPlan = async () => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${BASE_URL}/api/plan/my`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || '플랜 정보 조회 중 오류가 발생했습니다.');
    }

    return await response.json();
  } catch (error) {
    console.error('플랜 정보 조회 API 오류:', error);
    throw error;
  }
};

// 플랜 구독 연장 API 함수
export const extendPlanSubscription = async (planType, paymentInfo) => {
  try {
    const token = localStorage.getItem('token');
    
    // 회사 코드 가져오기 (localStorage에서)
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const companyCode = userInfo.middleCompanyCode;
    
    if (!companyCode) {
      console.warn('회사 코드를 찾을 수 없습니다. 구독 연장에는 회사 코드가 필요합니다.');
    }
    
    // 플랜 타입에 따른 이름 매핑 (전체 이름으로 수정)
    const planNameMapping = {
      'basic': 'TapGo Plus 1개월 플랜',
      'premium': 'TapGo Plus 6개월 플랜',
      'enterprise': 'TapGo Plus 12개월 플랜'
    };
    
    // 플랜 타입에 따른 연장 개월 수 매핑
    const monthsMapping = {
      'basic': 1,
      'premium': 6,
      'enterprise': 12
    };
    
    // 요청 본문 구성
    const requestBody = {
      plan_type: planType,
      plan_name: planNameMapping[planType] || 'TapGo Plus 구독 플랜',
      payment_key: paymentInfo.paymentKey,
      order_id: paymentInfo.orderId,
      amount: paymentInfo.amount,
      months: monthsMapping[planType] || 1, // 연장할 개월 수
      company_code: companyCode // 회사 코드 추가
    };
    
    console.log('구독 연장 요청 데이터:', requestBody);
    
    // API 요청 URL
    const apiUrl = `${BASE_URL}/api/plan/extend/toss`;
    console.log('요청 URL:', apiUrl);
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestBody)
    });

    console.log('응답 상태:', response.status);
    console.log('응답 URL:', response.url);
    
    // 응답 본문
    const responseText = await response.text();
    console.log('응답 본문 텍스트:', responseText);
    
    // 응답이 JSON 형식인 경우에만 파싱
    let extensionData;
    try {
      extensionData = JSON.parse(responseText);
      console.log('응답 데이터:', extensionData);
    } catch (parseError) {
      console.error('JSON 파싱 오류:', parseError);
      throw new Error('서버 응답을 처리할 수 없습니다: ' + responseText);
    }
    
    // 이미 처리된 결제인 경우에도 성공으로 간주
    if (!response.ok) {
      // 이미 처리된 결제 오류 확인
      if (extensionData.detail && (
         extensionData.detail.includes('이미 처리된 결제') || 
         extensionData.detail.includes('[S008]') ||
         extensionData.detail.includes('기존 요청을 처리중입니다')
      )) {
        console.log('이미 처리된 결제입니다. 플랜 정보를 조회합니다.');
        
        // 플랜 정보 조회 API 호출
        const planResponse = await fetch(`${BASE_URL}/api/plan/my`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (planResponse.ok) {
          const planData = await planResponse.json();
          console.log('현재 플랜 정보:', planData);
          
          // 성공 응답 형식으로 변환
          return {
            status: 'success',
            message: '이미 처리된 결제입니다. 현재 구독 정보를 표시합니다.',
            data: {
              plan: planData.data,
              company_code: planData.data.company_code,
              plan_name: planData.data.plan_name,
              payment_method: '신용카드',
              new_end_date: planData.data.end_date
            }
          };
        }
      }
      
      throw new Error(extensionData.detail || '구독 연장 중 오류가 발생했습니다.');
    }
    
    // 구독 연장 성공 시 로그만 출력
    if (extensionData.status === 'success') {
      console.log('구독 연장 성공, 새 종료일:', extensionData.data?.new_end_date);
    }

    return extensionData;
  } catch (error) {
    console.error('구독 연장 API 오류:', error);
    throw error;
  }
};

// 결제 내역 조회 API 함수
export const getPaymentHistory = async (page = 1, limit = 10, company_code = null) => {
  try {
    const token = localStorage.getItem('token');
    
    // 회사 코드가 제공되지 않은 경우 localStorage에서 가져오기
    let companyCode = company_code;
    if (!companyCode) {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      companyCode = userInfo.middleCompanyCode;
      
      if (!companyCode) {
        console.warn('회사 코드를 찾을 수 없습니다. localStorage에 저장된 회사 코드를 확인하세요.');
        throw new Error('회사 코드를 찾을 수 없습니다.');
      }
    }
    console.log('결제 내역 요청에 사용되는 회사 코드:', companyCode);
    
    // API 요청 URL - /payment/history 에서 /api/payment/history로 수정
    const url = `${BASE_URL}/api/plan/payment/history?page=${page}&limit=${limit}&company_code=${companyCode}`;
    console.log(`결제 내역 요청 URL: ${url}`);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    console.log('응답 상태:', response.status);
    
    // 응답 본문 처리
    const responseText = await response.text();
    
    // 응답이 비어있지 않은 경우에만 파싱 시도
    if (responseText && responseText.trim() !== '') {
      try {
        const data = JSON.parse(responseText);
        console.log('응답 데이터 구조:', Object.keys(data));
        return data;
      } catch (parseError) {
        console.error('JSON 파싱 오류:', parseError, '원본:', responseText);
        throw new Error('서버 응답을 처리할 수 없습니다.');
      }
    } else {
      console.warn('빈 응답이 반환되었습니다.');
      throw new Error('서버에서 데이터를 받지 못했습니다.');
    }
  } catch (error) {
    console.error('결제 내역 조회 API 오류:', error);
    throw error;
  }
};

// 결제 상세 정보 조회 API 함수
export const getPaymentDetail = async (paymentId, options = {}) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!paymentId) {
      throw new Error('결제 ID가 제공되지 않았습니다.');
    }
    
    console.log('결제 상세 정보 요청 ID:', paymentId);
    
    // API 요청 URL
    let url = `${BASE_URL}/api/plan/payment/${paymentId}`;
    
    // refund 파라미터 추가 (options.isRefund가 true인 경우)
    if (options.isRefund) {
      url += '?refund=true';
      console.log('환불 요청용 상세 정보 URL:', url);
    }
    
    console.log(`결제 상세 정보 요청 URL: ${url}`);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    console.log('응답 상태:', response.status);
    
    // 응답 본문 처리
    const responseText = await response.text();
    
    // 응답이 비어있지 않은 경우에만 파싱 시도
    if (responseText && responseText.trim() !== '') {
      try {
        const data = JSON.parse(responseText);
        console.log('결제 상세 정보:', data);
        
        return data;
      } catch (parseError) {
        console.error('JSON 파싱 오류:', parseError, '원본:', responseText);
        throw new Error('서버 응답을 처리할 수 없습니다.');
      }
    } else {
      console.warn('빈 응답이 반환되었습니다.');
      throw new Error('서버에서 데이터를 받지 못했습니다.');
    }
  } catch (error) {
    console.error('결제 상세 정보 조회 API 오류:', error);
    throw error;
  }
};

/**
 * 환불 요청을 제출하는 함수
 * @param {string} payment_id - 환불할 결제 ID
 * @param {string} reason - 환불 사유
 * @returns {Promise<Object>} - 환불 요청 응답
 */
export const submitRefundRequest = async (payment_id, reason) => {
  try {
    console.log('환불 요청 데이터:', { payment_id, reason });
    
    if (!payment_id) {
      throw new Error('결제 ID가 필요합니다.');
    }

    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('인증이 필요합니다. 다시 로그인해주세요.');
    }

    const requestData = {
      payment_id,
      reason
    };

    console.log('환불 요청 URL:', `${BASE_URL}/api/plan/refund`);
    console.log('환불 요청 데이터:', JSON.stringify(requestData));
    
    const response = await fetch(`${BASE_URL}/api/plan/refund`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestData)
    });

    console.log('환불 요청 응답 상태:', response.status);
    
    // 응답 데이터를 텍스트로 가져옵니다
    const responseText = await response.text();
    console.log('환불 요청 응답 원문:', responseText);
    
    // 응답이 JSON 형식인 경우에만 파싱 시도
    let data = {};
    if (responseText && responseText.trim()) {
      try {
        data = JSON.parse(responseText);
        console.log('환불 요청 응답 데이터:', data);
      } catch (parseError) {
        console.error('JSON 파싱 오류:', parseError);
      }
    }
    
    if (!response.ok) {
      // 에러 상세 정보를 콘솔에 출력
      console.error(`환불 요청 실패 (${response.status}):`, data.detail || data.message || responseText);
      
      // 401 오류 처리 (인증 오류)
      if (response.status === 401) {
        throw new Error('인증에 실패했습니다. 로그인이 만료되었거나 권한이 없습니다.');
      }
      
      // 400 Bad Request 오류 처리
      if (response.status === 400) {
        // 상세 에러 메시지 추출 시도
        const errorMessage = data.detail || data.message || responseText;
        throw new Error(`${errorMessage}`);
      }
      
      // 기타 오류
      throw new Error(data.detail || data.message || `환불 요청 실패 (${response.status}): ${responseText}`);
    }

    return data || { status: 'success', message: '환불 요청이 성공적으로 제출되었습니다.' };
  } catch (error) {
    console.error('환불 요청 오류:', error);
    throw error;
  }
};

/**
 * 관리자용 회사 목록 조회 API 함수
 * @returns {Promise<Object>} 회사 목록 응답
 */
export const getAdminCompanyList = async () => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('인증이 필요합니다. 다시 로그인해주세요.');
    }
    
    console.log('관리자용 회사 목록 요청 URL:', `${BASE_URL}/api/company/admin/companies`);
    
    const response = await fetch(`${BASE_URL}/api/company/admin/companies`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    console.log('응답 상태:', response.status);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || '회사 목록 조회 중 오류가 발생했습니다.');
    }
    
    const data = await response.json();
    console.log('응답 데이터:', data);
    
    return data;
  } catch (error) {
    console.error('관리자용 회사 목록 조회 API 오류:', error);
    throw error;
  }
};

/**
 * 회사 코드로 플랜 정보 조회 API 함수
 * @param {string} companyCode - 회사 코드
 * @returns {Promise<Object>} 플랜 정보 응답
 */
export const getCompanyPlanInfo = async (companyCode) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('인증이 필요합니다. 다시 로그인해주세요.');
    }
    
    console.log('회사 플랜 정보 요청 URL:', `${BASE_URL}/api/plan/${companyCode}`);
    
    const response = await fetch(`${BASE_URL}/api/plan/${companyCode}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    console.log('응답 상태:', response.status);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || '플랜 정보 조회 중 오류가 발생했습니다.');
    }
    
    const data = await response.json();
    console.log('플랜 정보 응답 데이터:', data);
    
    return data;
  } catch (error) {
    console.error('플랜 정보 조회 API 오류:', error);
    throw error;
  }
};

/**
 * 회사의 접속허용 상태 변경 API 함수
 * @param {string} companyCode - 회사 코드
 * @param {boolean} isAllowed - 접속허용 여부
 * @returns {Promise<Object>} 응답 데이터
 */
export const updateCompanyAccessStatus = async (companyCode, isAllowed) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('인증이 필요합니다. 다시 로그인해주세요.');
    }
    
    console.log(`회사 코드 ${companyCode}의 접속허용 상태를 ${isAllowed ? '허용' : '차단'}으로 변경 요청`);
    
    const response = await fetch(`${BASE_URL}/api/users/admin/middle-user/company/${companyCode}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ 
        isAccessAllowed: isAllowed 
      })
    });
    
    console.log('응답 상태:', response.status);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || '회사의 접속허용 상태 변경 중 오류가 발생했습니다.');
    }
    
    const data = await response.json();
    console.log('접속허용 상태 변경 응답:', data);
    
    return data;
  } catch (error) {
    console.error('회사 접속허용 상태 변경 API 오류:', error);
    throw error;
  }
};

/**
 * 중간관리자 목록 조회 API 함수 (관리자용)
 * @param {number} page - 페이지 번호
 * @param {number} limit - 페이지당 항목 수
 * @param {string} search - 검색어 (선택사항)
 * @returns {Promise<Object>} 중간관리자 목록 응답
 */
export const getAdminMiddleUsers = async (page = 1, limit = 100, search = null) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('인증이 필요합니다. 다시 로그인해주세요.');
    }
    
    // URL 구성
    let url = `${BASE_URL}/api/users/admin/middle-users?page=${page}&limit=${limit}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    
    console.log('중간관리자 목록 요청 URL:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    console.log('응답 상태:', response.status);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || '중간관리자 목록 조회 중 오류가 발생했습니다.');
    }
    
    const data = await response.json();
    console.log('중간관리자 목록 응답 데이터:', data);
    
    return data;
  } catch (error) {
    console.error('중간관리자 목록 조회 API 오류:', error);
    throw error;
  }
};

/**
 * 회사코드로 중간관리자 정보 조회 API 함수 (관리자용)
 * @param {string} companyCode - 회사 코드
 * @param {string} businessNumber - 사업자 번호 (회사 코드가 없을 경우 사용)
 * @returns {Promise<Object>} 중간관리자 정보 응답
 */
export const getMiddleUserByCompanyCode = async (companyCode, businessNumber = null) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('인증이 필요합니다. 다시 로그인해주세요.');
    }
    
    let url;
    
    // 회사 코드가 있으면 회사 코드로 조회, 없으면 사업자 번호로 조회
    if (companyCode && companyCode.trim() !== '') {
      url = `${BASE_URL}/api/users/admin/middle-users?company_code=${encodeURIComponent(companyCode)}`;
      console.log('회사 코드로 중간관리자 정보 요청 URL:', url);
    } else if (businessNumber && businessNumber.trim() !== '') {
      // 사업자 번호 그대로 사용 (하이픈 제거하지 않음)
      url = `${BASE_URL}/api/users/admin/middle-users?business_number=${encodeURIComponent(businessNumber)}`;
      console.log('사업자 번호로 중간관리자 정보 요청 URL:', url);
    } else {
      throw new Error('회사 코드 또는 사업자 번호가 필요합니다.');
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    console.log('응답 상태:', response.status);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || '중간관리자 정보 조회 중 오류가 발생했습니다.');
    }
    
    const data = await response.json();
    console.log('중간관리자 정보 조회 결과:', data);
    
    return data;
  } catch (error) {
    console.error('중간관리자 정보 조회 API 오류:', error);
    throw error;
  }
};

/**
 * 중간관리자 회사 정보 수정 API 함수
 * @param {string} companyCode - 회사 코드
 * @param {Object} updateData - 업데이트할 데이터
 * @returns {Promise<Object>} 응답 데이터
 */
export const updateMiddleUserCompany = async (companyCode, updateData) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('인증이 필요합니다. 다시 로그인해주세요.');
    }
    
    console.log('회사 정보 수정 요청:', companyCode, updateData);
    
    const response = await fetch(`${BASE_URL}/api/users/admin/middle-user/company/${companyCode}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updateData)
    });
    
    console.log('응답 상태:', response.status);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || '회사 정보 수정 중 오류가 발생했습니다.');
    }
    
    const data = await response.json();
    console.log('회사 정보 수정 응답:', data);
    
    return data;
  } catch (error) {
    console.error('회사 정보 수정 API 오류:', error);
    throw error;
  }
};

/**
 * 관리자용 플랜 목록 조회 API 함수
 * @param {number} page - 페이지 번호
 * @param {number} limit - 페이지당 항목 수
 * @param {string} search - 검색어 (선택사항)
 * @returns {Promise<Object>} 플랜 목록 응답
 */
export const getAdminPlans = async (page = 1, limit = 10, search = null) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('인증이 필요합니다. 다시 로그인해주세요.');
    }
    
    // URL 구성
    let url = `${BASE_URL}/api/plan/admin/plans?page=${page}&limit=${limit}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    
    console.log('관리자용 플랜 목록 요청 URL:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    console.log('응답 상태:', response.status);
    
    // 응답 본문 처리
    const responseText = await response.text();
    console.log('응답 본문 텍스트:', responseText);
    
    // 응답이 비어있지 않은 경우에만 파싱 시도
    if (responseText && responseText.trim() !== '') {
      try {
        const data = JSON.parse(responseText);
        console.log('플랜 목록 응답 데이터:', data);
        return data;
      } catch (parseError) {
        console.error('JSON 파싱 오류:', parseError, '원본:', responseText);
        throw new Error('서버 응답을 처리할 수 없습니다.');
      }
    } else {
      console.warn('빈 응답이 반환되었습니다.');
      throw new Error('서버에서 데이터를 받지 못했습니다.');
    }
  } catch (error) {
    console.error('관리자용 플랜 목록 조회 API 오류:', error);
    throw error;
  }
};

/**
 * 환불 요청 목록 조회 API 함수
 * @param {number} page - 페이지 번호
 * @param {number} limit - 페이지당 항목 수
 * @returns {Promise<Object>} 환불 요청 목록 응답
 */
export const getRefundRequests = async (page = 1, limit = 100) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('인증이 필요합니다. 다시 로그인해주세요.');
    }
    
    // URL 구성
    const url = `${BASE_URL}/api/plan/admin/refunds`;
    console.log('환불 요청 목록 요청 URL:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    console.log('응답 상태:', response.status);
    
    // 응답 본문 처리
    const responseText = await response.text();
    console.log('환불 요청 응답 본문 텍스트:', responseText);
    
    // 응답이 비어있지 않은 경우에만 파싱 시도
    if (responseText && responseText.trim() !== '') {
      try {
        const data = JSON.parse(responseText);
        console.log('환불 요청 목록 응답 데이터 구조:', Object.keys(data));
        console.log('환불 요청 목록 전체 데이터:', data);
        return data;
      } catch (parseError) {
        console.error('JSON 파싱 오류:', parseError, '원본:', responseText);
        throw new Error('서버 응답을 처리할 수 없습니다.');
      }
    } else {
      console.warn('빈 응답이 반환되었습니다.');
      throw new Error('서버에서 데이터를 받지 못했습니다.');
    }
  } catch (error) {
    console.error('환불 요청 목록 조회 API 오류:', error);
    throw error;
  }
};

/**
 * 환불 요청 상세 조회 API 함수
 * @param {string} refundId - 환불 요청 ID
 * @returns {Promise<Object>} 환불 요청 상세 정보
 */
export const getRefundDetail = async (refundId) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('인증이 필요합니다. 다시 로그인해주세요.');
    }
    
    // URL 구성
    const url = `${BASE_URL}/api/plan/admin/refund/${refundId}`;
    console.log('환불 요청 상세 조회 요청 URL:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    console.log('응답 상태:', response.status);
    
    // 응답 본문 처리
    const responseText = await response.text();
    console.log('환불 요청 상세 응답 본문 텍스트:', responseText);
    
    // 응답이 비어있지 않은 경우에만 파싱 시도
    if (responseText && responseText.trim() !== '') {
      try {
        const data = JSON.parse(responseText);
        console.log('환불 요청 상세 응답 데이터:', data);
        return data;
      } catch (parseError) {
        console.error('JSON 파싱 오류:', parseError, '원본:', responseText);
        throw new Error('서버 응답을 처리할 수 없습니다.');
      }
    } else {
      console.warn('빈 응답이 반환되었습니다.');
      throw new Error('서버에서 데이터를 받지 못했습니다.');
    }
  } catch (error) {
    console.error('환불 요청 상세 조회 API 오류:', error);
    throw error;
  }
};

/**
 * 환불 요청 거절 API 함수
 * @param {string} refundId - 환불 요청 ID
 * @param {string} reason - 거절 사유
 * @returns {Promise<Object>} - API 응답 데이터
 */
export const rejectRefundRequest = async (refundId, reason) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('인증이 필요합니다. 다시 로그인해주세요.');
    }
    
    // URL 구성
    const url = `${BASE_URL}/api/plan/admin/refund/${refundId}/reject`;
    console.log('환불 요청 거절 API 요청 URL:', url);
    
    // 요청 바디 구성
    const requestBody = {
      reason: reason || "관리자 거절",
      refund_amount: 0
    };
    
    console.log('환불 거절 요청 데이터:', requestBody);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestBody)
    });
    
    console.log('응답 상태:', response.status);
    
    // 응답 본문 처리
    const responseText = await response.text();
    console.log('환불 요청 거절 응답 본문 텍스트:', responseText);
    
    // 응답이 비어있지 않은 경우에만 파싱 시도
    if (responseText && responseText.trim() !== '') {
      try {
        const data = JSON.parse(responseText);
        console.log('환불 요청 거절 응답 데이터:', data);
        return data;
      } catch (parseError) {
        console.error('JSON 파싱 오류:', parseError, '원본:', responseText);
        throw new Error('서버 응답을 처리할 수 없습니다.');
      }
    } else {
      console.warn('빈 응답이 반환되었습니다.');
      throw new Error('서버에서 데이터를 받지 못했습니다.');
    }
  } catch (error) {
    console.error('환불 요청 거절 API 오류:', error);
    throw error;
  }
};

/**
 * 환불 요청 승인 API 함수
 * @param {string} refundId - 환불 요청 ID
 * @param {string} reason - 환불 사유
 * @param {number} refundAmount - 환불 금액
 * @returns {Promise<Object>} - API 응답 데이터
 */
export const approveRefundRequest = async (refundId, reason, refundAmount) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('인증이 필요합니다. 다시 로그인해주세요.');
    }
    
    // URL 구성
    const url = `${BASE_URL}/api/plan/admin/refund/${refundId}/approve`;
    console.log('환불 요청 승인 API 요청 URL:', url);
    
    // 요청 바디 구성
    const requestBody = {
      reason: reason || "관리자 승인",
      refund_amount: refundAmount || 0
    };
    
    console.log('환불 승인 요청 데이터:', requestBody);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestBody)
    });
    
    console.log('응답 상태:', response.status);
    
    // 응답 본문 처리
    const responseText = await response.text();
    console.log('환불 요청 승인 응답 본문 텍스트:', responseText);
    
    // 응답이 비어있지 않은 경우에만 파싱 시도
    if (responseText && responseText.trim() !== '') {
      try {
        const data = JSON.parse(responseText);
        console.log('환불 요청 승인 응답 데이터:', data);
        return data;
      } catch (parseError) {
        console.error('JSON 파싱 오류:', parseError, '원본:', responseText);
        throw new Error('서버 응답을 처리할 수 없습니다.');
      }
    } else {
      console.warn('빈 응답이 반환되었습니다.');
      throw new Error('서버에서 데이터를 받지 못했습니다.');
    }
  } catch (error) {
    console.error('환불 요청 승인 API 오류:', error);
    throw error;
  }
};

/**
 * 사업자번호로 중간관리자 회사 정보 수정 API 함수
 * @param {string} businessNumber - 사업자 번호
 * @param {Object} updateData - 업데이트할 데이터
 * @returns {Promise<Object>} 응답 데이터
 */
export const updateMiddleUserCompanyByBusinessNumber = async (businessNumber, updateData) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('인증이 필요합니다. 다시 로그인해주세요.');
    }
    
    console.log('사업자번호로 회사 정보 수정 요청:', businessNumber, updateData);
    
    const response = await fetch(`${BASE_URL}/api/users/admin/middle-user/business-number/${businessNumber}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updateData)
    });
    
    console.log('응답 상태:', response.status);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || '회사 정보 수정 중 오류가 발생했습니다.');
    }
    
    const data = await response.json();
    console.log('사업자번호로 회사 정보 수정 응답:', data);
    
    return data;
  } catch (error) {
    console.error('사업자번호로 회사 정보 수정 API 오류:', error);
    throw error;
  }
};

/**
 * 회사 데이터 삭제 API 함수 (관리자용)
 * @param {string} companyCode - 회사 코드 (선택사항)
 * @param {string} businessNumber - 사업자 번호 (선택사항)
 * @returns {Promise<Object>} 응답 데이터
 */
export const deleteCompany = async (companyCode, businessNumber) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('인증이 필요합니다. 다시 로그인해주세요.');
    }
    
    // 쿼리 파라미터 구성
    let queryParams = [];
    if (companyCode) {
      queryParams.push(`company_code=${encodeURIComponent(companyCode)}`);
    }
    if (businessNumber) {
      queryParams.push(`business_number=${encodeURIComponent(businessNumber)}`);
    }
    
    const url = `${BASE_URL}/api/company/admin/delete?${queryParams.join('&')}`;
    console.log('회사 삭제 요청 URL:', url);
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    console.log('응답 상태:', response.status);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || '회사 데이터 삭제 중 오류가 발생했습니다.');
    }
    
    const data = await response.json().catch(() => ({ status: 'success', message: '성공적으로 삭제되었습니다.' }));
    console.log('회사 삭제 응답:', data);
    
    return data;
  } catch (error) {
    console.error('회사 삭제 API 오류:', error);
    throw error;
  }
};

/**
 * 회사 월별 근로자 정보 Excel 다운로드 API 함수
 * @param {string} companyCode - 회사 코드
 * @param {number} year - 년도
 * @param {number} month - 월
 * @param {string} email - 특정 직원 이메일 (선택적)
 * @returns {Promise<Blob>} - Excel 파일 Blob
 */
export const downloadCompanyMonthlyExcel = async (companyCode, year, month, email = null) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('인증이 필요합니다. 다시 로그인해주세요.');
    }
    
    // 기본 URL에 년도와 월 파라미터 추가
    let url = `${BASE_URL}/api/excel/company/monthly/excel?year=${year}&month=${month}`;
    
    // 이메일이 제공된 경우 URL에 추가
    if (email) {
      url += `&email=${encodeURIComponent(email)}`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Excel 다운로드 중 오류가 발생했습니다.');
    }

    return response.blob();
  } catch (error) {
    console.error('Excel 다운로드 API 오류:', error);
    throw error;
  }
};

/**
 * 관리자용 수익 결제 로그 조회 API 함수
 * @param {number} year - 조회할 연도
 * @param {number} month - 조회할 월(1-12)
 * @returns {Promise<Object>} 응답 데이터
 */
export const getRevenuePaymentLogs = async (year, month) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('인증이 필요합니다. 다시 로그인해주세요.');
    }
    
    const response = await fetch(`${BASE_URL}/api/revenue/payment-logs?year=${year}&month=${month}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || '수익 데이터 조회 중 오류가 발생했습니다.');
    }
    
    const data = await response.json();
    console.log('수익 데이터 응답:', data);
    
    return data;
  } catch (error) {
    console.error('수익 데이터 조회 API 오류:', error);
    throw error;
  }
};

/**
 * 공휴일 정보 조회 API 함수
 * @param {number} year - 조회할 연도
 * @param {number} month - 조회할 월(1-12)
 * @returns {Promise<Object>} 응답 데이터
 */
export const getHolidays = async (year, month) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('인증이 필요합니다. 다시 로그인해주세요.');
    }
    
    const response = await fetch(`${BASE_URL}/api/holidays/?year=${year}&month=${month}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || '공휴일 정보 조회 중 오류가 발생했습니다.');
    }
    
    const data = await response.json();
    console.log('공휴일 데이터 응답:', data);
    
    return data;
  } catch (error) {
    console.error('공휴일 정보 조회 API 오류:', error);
    throw error;
  }
};

/**
 * 회사 코드로 해당 회사 소속 사용자들의 공지사항 동의 상태 업데이트 API 함수
 * @param {string} companyCode - 회사 코드
 * @param {boolean} noticeAgreed - 공지사항 동의 상태
 * @returns {Promise<Object>} 응답 데이터
 */
export const updateCompanyUsersNoticeAgreement = async (companyCode, noticeAgreed = true) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('인증이 필요합니다. 다시 로그인해주세요.');
    }
    
    const response = await fetch(`${BASE_URL}/api/users/company/${companyCode}/notice-agreement`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ noticeAgreed })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || '사용자 공지사항 동의 상태 업데이트 중 오류가 발생했습니다.');
    }
    
    const data = await response.json();
    console.log('사용자 공지사항 동의 상태 업데이트 응답:', data);
    
    return data;
  } catch (error) {
    console.error('사용자 공지사항 동의 상태 업데이트 API 오류:', error);
    throw error;
  }
};

// SMS 인증번호 발송 API 함수
export const sendVerificationSMS = async (phoneNumber) => {
  console.log('SMS 인증번호 발송 API 요청:', phoneNumber);
  console.log('BASE_URL:', BASE_URL);
  try {
    const response = await fetch(`${BASE_URL}/api/sms/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ phone: phoneNumber }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || '인증번호 발송 중 오류가 발생했습니다.');
    }

    return await response.json();
  } catch (error) {
    console.error('SMS 인증번호 발송 API 오류:', error);
    throw error;
  }
};

// SMS 인증번호 확인 API 함수
export const verifyVerificationCode = async (phoneNumber, verificationCode) => {
  console.log('SMS 인증번호 확인 API 요청:');
  console.log('- 전화번호:', phoneNumber);
  console.log('- 인증코드:', verificationCode);
  
  try {
    const response = await fetch(`${BASE_URL}/api/sms/verify/check`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ 
        phone: phoneNumber,
        code: verificationCode 
      }),
    });

    console.log('응답 상태 코드:', response.status);
    
    // 응답 본문 처리
    const responseText = await response.text();
    console.log('응답 본문 텍스트:', responseText);
    
    // 응답이 JSON 형식인 경우에만 파싱
    let data = {};
    if (responseText && responseText.trim()) {
      try {
        data = JSON.parse(responseText);
        console.log('SMS 인증번호 확인 응답 데이터:', data);
      } catch (parseError) {
        console.error('JSON 파싱 오류:', parseError);
        throw new Error('서버 응답을 처리할 수 없습니다: ' + responseText);
      }
    }

    if (!response.ok) {
      throw new Error(data.detail || data.message || '인증번호 확인 중 오류가 발생했습니다.');
    }

    return data;
  } catch (error) {
    console.error('SMS 인증번호 확인 API 오류:', error);
    throw error;
  }
};

// 회사 정보 조회 API 함수 (회원가입용)
export const getPublicCompanyInfo = async (companyCode) => {
  console.log('회사 정보 조회 API 요청:', companyCode);
  
  try {
    const response = await fetch(`${BASE_URL}/api/company/public/info/${companyCode}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      }
    });
    
    console.log('응답 상태 코드:', response.status);
    
    // 응답 본문 처리
    const responseText = await response.text();
    console.log('회사 정보 응답 본문 텍스트:', responseText);
    
    // 응답이 JSON 형식인 경우에만 파싱
    let data = {};
    if (responseText && responseText.trim()) {
      try {
        data = JSON.parse(responseText);
        console.log('회사 정보 응답 데이터:', data);
      } catch (parseError) {
        console.error('JSON 파싱 오류:', parseError);
        throw new Error('서버 응답을 처리할 수 없습니다: ' + responseText);
      }
    }
    
    if (!response.ok) {
      throw new Error(data.detail || '회사 정보 조회에 실패했습니다.');
    }
    
    return {
      success: true,
      message: data.message || '회사 정보 조회 성공',
      data: data.data,
      statusCode: response.status
    };
  } catch (error) {
    console.error('회사 정보 조회 API 오류:', error);
    return {
      success: false,
      message: error.message || '네트워크 오류가 발생했습니다.',
      statusCode: 500
    };
  }
};

// 회사 계약 정보 조회 API 함수
export const getCompanyContract = async (companyCode) => {
  console.log('회사 계약 정보 조회 API 요청:', companyCode);
  
  try {
    if (!companyCode) {
      return {
        success: false,
        message: '회사 코드가 없습니다.',
        statusCode: 400
      };
    }
    
    const response = await fetch(`${BASE_URL}/api/company/contract/${companyCode}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      }
    });
    
    console.log('응답 상태 코드:', response.status);
    
    // 응답 본문 처리
    const responseText = await response.text();
    console.log('회사 계약 정보 응답 본문 텍스트:', responseText);
    
    // 응답이 JSON 형식인 경우에만 파싱
    let data = {};
    if (responseText && responseText.trim()) {
      try {
        data = JSON.parse(responseText);
        console.log('회사 계약 정보 응답 데이터:', data);
      } catch (parseError) {
        console.error('JSON 파싱 오류:', parseError);
        throw new Error('서버 응답을 처리할 수 없습니다: ' + responseText);
      }
    }
    
    if (!response.ok) {
      throw new Error(data.detail || '회사 계약 정보 조회에 실패했습니다.');
    }
    
    return {
      success: true,
      message: data.message || '회사 계약 정보 조회 성공',
      data: data.data,
      statusCode: response.status
    };
  } catch (error) {
    console.error('회사 계약 정보 조회 API 오류:', error);
    return {
      success: false,
      message: error.message || '네트워크 오류가 발생했습니다.',
      statusCode: 500
    };
  }
};

// 일반 회원 등록 API 함수
export const registerUserCommon = async (userData) => {
  console.log('일반 회원 등록 API 요청:', userData);
  
  try {
    const response = await fetch(`${BASE_URL}/api/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(userData),
    });
    
    console.log('응답 상태 코드:', response.status);
    
    // 응답 본문 처리
    const responseText = await response.text();
    console.log('회원 등록 응답 본문 텍스트:', responseText);
    
    // 응답이 JSON 형식인 경우에만 파싱
    let data = {};
    if (responseText && responseText.trim()) {
      try {
        data = JSON.parse(responseText);
        console.log('회원 등록 응답 데이터:', data);
      } catch (parseError) {
        console.error('JSON 파싱 오류:', parseError);
        throw new Error('서버 응답을 처리할 수 없습니다: ' + responseText);
      }
    }
    
    if (response.status === 201) {
      return {
        success: true,
        message: data.message || '회원가입이 완료되었습니다.',
        data: data,
        statusCode: response.status
      };
    } else {
      throw new Error(data.detail || '회원가입에 실패했습니다.');
    }
  } catch (error) {
    console.error('회원 등록 API 오류:', error);
    return {
      success: false,
      message: error.message || '네트워크 오류가 발생했습니다.',
      statusCode: 500
    };
  }
};

// 비밀번호 재설정 요청 API 함수
export const requestPasswordReset = async (email) => {
  try {
    const response = await fetch(`${BASE_URL}/api/auth/request-reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || '비밀번호 재설정 요청 중 오류가 발생했습니다.');
    }

    return await response.json();
  } catch (error) {
    console.error('비밀번호 재설정 요청 API 오류:', error);
    throw error;
  }
};

// 비밀번호 재설정 토큰 검증 API 함수
export const verifyResetToken = async (token) => {
  try {
    const response = await fetch(`${BASE_URL}/api/auth/verify-reset-token/${token}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || '토큰 검증에 실패했습니다.');
    }

    return await response.json();
  } catch (error) {
    console.error('토큰 검증 API 오류:', error);
    throw error;
  }
};

// 비밀번호 재설정 API 함수
export const resetPassword = async (token, newPassword) => {
  try {
    const response = await fetch(`${BASE_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token,
        new_password: newPassword,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || '비밀번호 재설정에 실패했습니다.');
    }

    return await response.json();
  } catch (error) {
    console.error('비밀번호 재설정 API 오류:', error);
    throw error;
  }
};

// 기타 API 함수들을 추가할 수 있음 